import { useMemo } from "react";
import type { StorageScope } from "../../../../services/api/instances/storageApi/types/service_types";
import { useAppSelector } from "../../../../services/redux/hooks/use-selector";
import {
  makeSelectAllObjects,
  makeSelectMenuItems,
  makeSelectSelectedObject,
  makeSelectSelectedObjectVersion,
  makeSelectServiceField,
  selectCurrentService,
} from "../selectors";

export const useStorageSelectors = <S extends StorageScope>(service: S) => {
  const selectMenuItems = useMemo(
    () => makeSelectMenuItems(service),
    [service],
  );
  const selectAllObjects = useMemo(
    () => makeSelectAllObjects(service),
    [service],
  );
  const selectSelectedId = useMemo(
    () => makeSelectServiceField<"selectedId">(service, "selectedId"),
    [service],
  );
  const selectEntities = useMemo(
    () => makeSelectServiceField<"data">(service, "data"),
    [service],
  );
  const selectSelectedObject = useMemo(
    () => makeSelectSelectedObject(service),
    [service],
  );
  const selectLoading = useMemo(
    () => makeSelectServiceField<"loading">(service, "loading"),
    [service],
  );
  const selectVersions = useMemo(
    () => makeSelectServiceField<"versions">(service, "versions"),
    [service],
  );
  const selectSelectedObjectVersion = useMemo(
    () => makeSelectSelectedObjectVersion(service),
    [service],
  );

  const menuItems = useAppSelector(selectMenuItems);
  const isLoading = useAppSelector(selectLoading);
  const objects = useAppSelector(selectAllObjects);
  const selectedObject = useAppSelector(selectSelectedObject);
  const versions = useAppSelector(selectVersions);
  const selectedVersion = useAppSelector(selectSelectedObjectVersion);
  const selectedId = useAppSelector(selectSelectedId);
  const entities = useAppSelector(selectEntities);
  const currentService = useAppSelector(selectCurrentService);

  return {
    menuItems,
    objects,
    selectedObject,
    selectedId,
    entities,
    versions,
    currentService,
    selectedVersion,
    isLoading,
  };
};
